import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, CheckCircle, ChevronRight, Building2, Navigation } from "lucide-react";
import "./MobileFirstPickupStep.scss";

export default function MobileFirstPickupStep({ 
  selectedAddress, 
  setSelectedAddress, 
  addresses, 
  hasMultipleAddresses 
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();

  // El wizard se encarga de la auto-selección, este componente solo muestra el estado
  const manyAddresses = Array.isArray(addresses) && addresses.length > 1 ? true : !!hasMultipleAddresses;

  const formatAddressLabel = (address) => {
    if (address.address) {
      return `${address.address}, ${address.municipality}, ${address.state}`.toUpperCase();
    }
    return `${address.street_name} ${address.street_number}, ${address.municipality?.name}, ${address.city?.name}`.toUpperCase();
  };

  const getAddressDisplay = (address) => {
    if (address.address) {
      return {
        street: address.address,
        city: `${address.municipality}, ${address.state}`
      };
    }
    return {
      street: `${address.street_name} ${address.street_number}`,
      city: `${address.municipality?.name}, ${address.city?.name}`
    };
  };

  // Si solo hay una dirección, mostrar vista simplificada
  if (!manyAddresses && Array.isArray(addresses) && addresses.length === 1) {
    const address = addresses[0];
    const display = getAddressDisplay(address);
    
    return (
      <div className="mobile-pickup-single">
        
        <div className="mobile-pickup-single__card">
          <div className="mobile-pickup-single__card-header">
            <div className="mobile-pickup-single__card-icon">
              <Building2 size={20} />
            </div>
            <span className="mobile-pickup-single__card-title">{t("Dirección de retiro")}</span>
          </div>
          
          <div className="mobile-pickup-single__card-content">
            <div className="mobile-pickup-single__address-row">
              <p className="mobile-pickup-single__address">{display.street}</p>
              <span className="mobile-pickup-pill">
                <CheckCircle size={14} />
                {t("Seleccionada automáticamente")}
              </span>
            </div>
            <p className="mobile-pickup-single__city">{display.city}</p>
          </div>
        </div>
      </div>
    );
  }

  // Si hay múltiples direcciones, mostrar selector (dropdown accesible)
  return (
    <div className="mobile-pickup-multiple">
      <div className="mobile-pickup-multiple__header">
        <div className="mobile-pickup-multiple__icon">
          <Navigation size={28} />
        </div>
        <div className="mobile-pickup-multiple__content">
          <h3>{t("Selecciona la dirección de retiro")}</h3>
          <p>{t("Elige desde dónde se recogerán los paquetes")}</p>
        </div>
      </div>

      <div className="mobile-pickup-multiple__selector">
        <button
          className={`mobile-pickup-multiple__trigger ${isExpanded ? 'expanded' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-haspopup="listbox"
        >
          <div className="mobile-pickup-multiple__trigger-content">
            <div className="mobile-pickup-multiple__trigger-icon">
              <MapPin size={20} />
            </div>
            <div className="mobile-pickup-multiple__trigger-text">
              {selectedAddress ? (
                <div>
                  <span className="mobile-pickup-multiple__trigger-label">{t("Dirección seleccionada")}</span>
                  <span className="mobile-pickup-multiple__trigger-address">
                    {formatAddressLabel(selectedAddress.value)}
                  </span>
                </div>
              ) : (
                <div>
                  <span className="mobile-pickup-multiple__trigger-label">{t("Selecciona una dirección")}</span>
                  <span className="mobile-pickup-multiple__trigger-placeholder">
                    {t("Toca para ver las opciones disponibles")}
                  </span>
                </div>
              )}
            </div>
          </div>
          <ChevronRight 
            size={20} 
            className={`mobile-pickup-multiple__chevron ${isExpanded ? 'rotated' : ''}`} 
          />
        </button>

        {isExpanded && (
          <div className="mobile-pickup-multiple__dropdown" role="listbox">
            {addresses.map((address, index) => {
              const display = getAddressDisplay(address);
              const isSelected = selectedAddress?.value === address;
              
              return (
                <button
                  key={index}
                  className={`mobile-pickup-multiple__option ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedAddress({ value: address, label: formatAddressLabel(address) });
                    setIsExpanded(false);
                  }}
                  role="option"
                  aria-selected={isSelected}
                >
                  <div className="mobile-pickup-multiple__option-content">
                    <div className="mobile-pickup-multiple__option-icon">
                      <MapPin size={18} />
                    </div>
                    <div className="mobile-pickup-multiple__option-text">
                      <span className="mobile-pickup-multiple__option-title">
                        {t("Dirección {{n}}", { n: index + 1 })}
                      </span>
                      <span className="mobile-pickup-multiple__option-address">
                        {display.street}
                      </span>
                      <span className="mobile-pickup-multiple__option-city">
                        {display.city}
                      </span>
                    </div>
                  </div>
                  {isSelected && (
                    <CheckCircle size={18} className="mobile-pickup-multiple__check" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
